import React from 'react';
import { Heart, HeartHandshake, Briefcase, DollarSign, Home, GraduationCap, Sparkles, Plane } from 'lucide-react';
import '../../styles/website/DomainsSection.css';

const domains = [
  { 
    id: 1, 
    name: 'Health', 
    Icon: Heart,
    color: '#ff6b6b', 
    position: 'top',
    description: 'Well-being, vitality, diseases, recovery, longevity.'
  },
  { 
    id: 2, 
    name: 'Relationship & Married Life', 
    Icon: HeartHandshake,
    color: '#ff66c4', 
    position: 'top-right',
    description: 'Relationship harmony, marriage timing, married life quality, spouse dynamics.'
  },
  { 
    id: 3, 
    name: 'Career & Profession', 
    Icon: Briefcase,
    color: '#4ecdc4', 
    position: 'right',
    description: 'Career direction, promotions, skills, job changes, leadership, freelance.'
  },
  { 
    id: 4, 
    name: 'Wealth & Assets', 
    Icon: DollarSign,
    color: '#ffd93d', 
    position: 'bottom-right',
    description: 'Money flow, earnings, investments, property, assets, financial growth.'
  },
  { 
    id: 5, 
    name: 'Family Life & Children', 
    Icon: Home,
    color: '#95e1d3', 
    position: 'bottom',
    description: 'Family environment, parental influence, siblings, children planning, childbirth.'
  },
  { 
    id: 6, 
    name: 'Education', 
    Icon: GraduationCap,
    color: '#a8e6cf', 
    position: 'bottom-left',
    description: 'Learning, studies, degrees, intellectual growth, academic success.'
  },
  { 
    id: 7, 
    name: 'Spirituality & Religion', 
    Icon: Sparkles,
    color: '#c7b3ff', 
    position: 'left',
    description: 'Inner growth, faith, rituals, meaning, clarity, purpose.'
  },
  { 
    id: 8, 
    name: 'Travel & Settlement', 
    Icon: Plane,
    color: '#6bcf7f', 
    position: 'top-left',
    description: 'Relocation, foreign opportunities, travel, migration, settlement.'
  }
];

const DomainsSection = () => {
  return (
    <section className="domains-section" id="domains">
      <h2 className="domains-title">Life Domains</h2>
      <p className="domains-subtitle">Navigate every aspect of your journey</p>
      
      <div className="hexagon-container">
        {/* Central Astrology Chart */}
        <div className="central-chart">
          <svg viewBox="0 0 200 200" className="astrology-chart">
            <defs>
              <linearGradient id="chartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#00f5ff', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: '#5b8fd8', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#8b7dd8', stopOpacity: 1 }} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            <circle cx="100" cy="100" r="80" fill="none" stroke="url(#chartGradient)" strokeWidth="2" filter="url(#glow)" opacity="0.6" />
            <circle cx="100" cy="100" r="60" fill="none" stroke="url(#chartGradient)" strokeWidth="1.5" opacity="0.5" />
            <circle cx="100" cy="100" r="40" fill="none" stroke="url(#chartGradient)" strokeWidth="1" opacity="0.4" />
            
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 - 90) * (Math.PI / 180);
              return (
                <line
                  key={i}
                  x1="100"
                  y1="100"
                  x2={100 + Math.cos(angle) * 80}
                  y2={100 + Math.sin(angle) * 80}
                  stroke="url(#chartGradient)"
                  strokeWidth="0.5"
                  opacity="0.3"
                />
              );
            })}
            
            <circle cx="100" cy="100" r="25" fill="rgba(0, 245, 255, 0.1)" stroke="url(#chartGradient)" strokeWidth="2" filter="url(#glow)" />
            <ellipse cx="100" cy="100" rx="12" ry="18" fill="url(#chartGradient)" opacity="0.8" />
            <circle cx="100" cy="100" r="8" fill="#ffffff" />
            <circle cx="100" cy="100" r="4" fill="#000033" />
            
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 - 90) * (Math.PI / 180);
              return (
                <circle
                  key={`zodiac-${i}`}
                  cx={100 + Math.cos(angle) * 70}
                  cy={100 + Math.sin(angle) * 70}
                  r="3"
                  fill="url(#chartGradient)"
                  opacity="0.8"
                  filter="url(#glow)"
                />
              );
            })}
          </svg>
          
          <svg className="connection-lines" viewBox="0 0 600 600">
            <defs>
              <linearGradient id="lineGradient">
                <stop offset="0%" style={{ stopColor: '#00f5ff', stopOpacity: 0.1 }} />
                <stop offset="100%" style={{ stopColor: '#00f5ff', stopOpacity: 0.5 }} />
              </linearGradient>
            </defs>
            {domains.map((domain, i) => {
              const angle = (i * 45 - 90) * (Math.PI / 180);
              return (
                <line
                  key={`line-${domain.id}`}
                  x1="300"
                  y1="300"
                  x2={300 + Math.cos(angle) * 200}
                  y2={300 + Math.sin(angle) * 200}
                  stroke="url(#lineGradient)"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                  className="connection-line"
                />
              );
            })}
          </svg>
        </div>
        
        {domains.map((domain) => {
          const IconComponent = domain.Icon;
          return (
            <div key={domain.id} className={`hexagon-card hex-${domain.position}`}>
              <div className="hexagon-shape">
                <svg viewBox="0 0 100 100" className="hexagon-svg">
                  <defs>
                    <linearGradient id={`hexGrad-${domain.id}`}>
                      <stop offset="0%" style={{ stopColor: domain.color, stopOpacity: 0.4 }} />
                      <stop offset="100%" style={{ stopColor: '#00f5ff', stopOpacity: 0.1 }} />
                    </linearGradient>
                  </defs>
                  <polygon
                    points="50 5, 90 25, 90 75, 50 95, 10 75, 10 25"
                    fill={`url(#hexGrad-${domain.id})`}
                    stroke={domain.color}
                    strokeWidth="2"
                    className="hexagon-polygon"
                  />
                </svg>
                <div className="hexagon-content">
                  <div className="hex-icon-wrapper">
                    <IconComponent className="hex-icon" size={32} style={{ color: domain.color }} />
                  </div>
                  <div className="hex-name" style={{ color: domain.color }}>
                    {domain.name}
                  </div>
                  <div className="hex-description" style={{ borderColor: domain.color, color: domain.color }}>
                    {domain.description}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        <div className="zodiac-symbols">
          {['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'].map((symbol, i) => (
            <div key={`symbol-${i}`} className={`zodiac-symbol symbol-${i}`}>
              {symbol}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DomainsSection;
